import { useState } from "react";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Stack,
  Typography,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import FormProvider, {
  RHFTextField,
  RHFCodes,
} from "src/components/hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  postDriverSendCode,
  postDriverVerifyCode,
} from "src/redux/slices/driverAuth";
import { useSnackbar } from "notistack";

type Props = {
  ownerId: string;
};

export default function DriverLogin({ ownerId }: Props) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const driverAuth = useSelector((state: RootState) => state.driverAuth);
  const [step, setStep] = useState(0);

  const PhoneSchema = Yup.object().shape({
    phone: Yup.string()
      .required("WhatsApp obrigatório")
      .test("valid-phone", "Informe um número válido", (value) => {
        const digits = (value || "").replace(/\D/g, "");
        return digits.length >= 10;
      }),
  });

  const phoneMethods = useForm({
    resolver: yupResolver(PhoneSchema),
    defaultValues: { phone: "" },
  });

  const CodeSchema = Yup.object().shape({
    code1: Yup.string().required("Código é obrigatório"),
    code2: Yup.string().required("Código é obrigatório"),
    code3: Yup.string().required("Código é obrigatório"),
    code4: Yup.string().required("Código é obrigatório"),
    code5: Yup.string().required("Código é obrigatório"),
    code6: Yup.string().required("Código é obrigatório"),
  });

  const codeMethods = useForm({
    mode: "onChange",
    resolver: yupResolver(CodeSchema),
    defaultValues: {
      code1: "",
      code2: "",
      code3: "",
      code4: "",
      code5: "",
      code6: "",
    },
  });

  const handlePhoneSubmit = async (data: any) => {
    try {
      await dispatch(
        postDriverSendCode({
          ownerId,
          phone: "55" + data.phone.replace(/\D/g, ""),
        })
      ).unwrap();
      setStep(1);
    } catch (error: any) {
      const message = error?.message || error || "Erro ao solicitar código.";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleCodeSubmit = async (data: any) => {
    try {
      const code = Object.values(data).join("");
      const phone = phoneMethods.getValues("phone");
      await dispatch(
        postDriverVerifyCode({
          ownerId,
          phone: "55" + phone.replace(/\D/g, ""),
          code,
        })
      ).unwrap();
    } catch (error: any) {
      const message = error?.message || error || "Código inválido ou expirado.";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  return (
    <Stack spacing={3} sx={{ py: 4 }}>
      <Typography variant="h4">Entrar como motorista</Typography>
      <Typography variant="body2" color="text.secondary">
        Receba um código no seu WhatsApp para acessar seus pedidos.
      </Typography>

      {step === 0 && (
        <FormProvider
          methods={phoneMethods}
          onSubmit={phoneMethods.handleSubmit(handlePhoneSubmit)}
        >
          <Stack spacing={2}>
            <RHFTextField
              mask="(99) 99999-9999"
              name="phone"
              label="WhatsApp"
              type="tel"
              inputProps={{ inputMode: "numeric" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">+55</InputAdornment>
                ),
              }}
            />
            <LoadingButton
              fullWidth
              variant="contained"
              type="submit"
              loading={driverAuth.isLoading}
            >
              Enviar código
            </LoadingButton>
          </Stack>
        </FormProvider>
      )}

      {step === 1 && (
        <FormProvider
          methods={codeMethods}
          onSubmit={codeMethods.handleSubmit(handleCodeSubmit)}
        >
          <Stack spacing={2}>
            <RHFCodes
              keyName="code"
              inputs={["code1", "code2", "code3", "code4", "code5", "code6"]}
            />

            {Object.values(codeMethods.formState.errors).length > 0 && (
              <FormHelperText error>Código é obrigatório</FormHelperText>
            )}

            <LoadingButton
              fullWidth
              variant="contained"
              type="submit"
              loading={driverAuth.isLoading}
            >
              Verificar código
            </LoadingButton>

            <LoadingButton
              fullWidth
              variant="text"
              onClick={phoneMethods.handleSubmit(handlePhoneSubmit)}
              loading={driverAuth.isLoading}
            >
              Reenviar código
            </LoadingButton>
          </Stack>
        </FormProvider>
      )}
    </Stack>
  );
}
